import React, { useCallback, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { colors } from '../../style';
import { ListView } from '@progress/kendo-react-listview';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { PageChangeEvent } from '@progress/kendo-react-data-tools';
import PagerSection from '../base/pager-section';
import DocumentGraphic from '../../images/document-in-cloud.png';
import { ButtonStyledLink, Dialog } from '../base/simple-styled-components';
import {
  GridContainer,
  ColumnContainer,
  ContentContainer,
  ListViewBody,
  TitleBar,
  Title,
  Text,
  ItalicText,
  HeaderTextSmall,
  NoDocumentsContainer,
  LoadingCover,
} from '../base/tw-components';
import { enums, get } from '../../utils';
import ItemRender, { IItemRenderProps } from './item-render';
import * as dm from '../../data-models';
import { PersonDataContext } from '../../contexts/person-data-context';
import ClaimsDropDownList from '../base/claims-drop-down-list';
import useRefreshHandler from '../../hooks/use-refresh-handler';
import { FadeLoader } from 'react-spinners';
import { useUploadDocuments } from '../../hooks/use-upload-documents';
import { UploadStatus } from '../../utils/upload-activities';

/**
 * Interfaces
 */
export interface DocumentProps extends dm.Document {
  caseId?: string;
  claimId?: string;
  leaveId?: string;
  receivedDate?: string;
  pending?: boolean;
  uploadStatus?: UploadStatus;
}

interface DocumentsComponentProps {
  displayModalTest?: boolean;
}

const domIdsStatic = {
  rootNode: 'documents',
};

const normalize = (s?: string) => (s ?? '').toString().trim().toLowerCase();

const ntnRoot = (id: string) => {
  const parts = normalize(id).split('-');
  return parts.slice(0, 2).join('-'); // first 2 segments = NTN + number
};

const sameNtnFamily = (a: string, b: string) => ntnRoot(a) === ntnRoot(b);

const hasBaseNtn = (source?: string, prefix?: string) =>
  !!normalize(source) &&
  !!normalize(prefix) &&
  normalize(source).startsWith(normalize(prefix));

export const sortDescending = (a: any, b: any) => {
  const d1 = new Date(a.receivedDate);
  const d2 = new Date(b.receivedDate);

  return d1 < d2 ? 1 : -1;
};

const extractSelectedNTN = (id?: string): string => {
  if (!id) return '';
  const re = /^(?<ntn>NTN-[^-]+)(?:-.+)?/i;
  const m = re.exec(id);
  return m?.groups?.ntn ?? '';
};

const defaultOption = {
  caseId: 'View all claims',
  display: 'View All claims',
};

const DocumentItemRender = React.memo((props: IItemRenderProps) => (
  <ItemRender {...props} />
));

const Documents = (p: DocumentsComponentProps) => {
  const history = useHistory();
  const { ntn: ntnToRouteRaw } = useParams<{ ntn?: string }>();
  const ntnToRoute = React.useMemo(
    () => (ntnToRouteRaw ? decodeURIComponent(ntnToRouteRaw) : ''),
    [ntnToRouteRaw],
  );
  const { personData } = useContext(PersonDataContext);
  const { isRefreshing } = useRefreshHandler([
    enums.RefreshDetailTypes.cases,
    enums.RefreshDetailTypes.documents,
  ]);

  const [initialDocuments, setInitialDocuments] = React.useState<
    DocumentProps[]
  >([]);
  const [selectedClaim, setSelectedClaim] = React.useState<any>(defaultOption);

  const [displayModal, setDisplayModal] = React.useState(
    p.displayModalTest || false,
  );
  const [modalLink, setModalLink] = React.useState('');
  const [page, setPage] = React.useState({
    skip: 0,
    take: 10,
  });

  const { skip, take } = page; //Used for Pagination

  const claimList = React.useMemo(
    () => [
      ...(personData?.cases?.claims || []),
      ...(personData?.cases?.leaves || []),
      ...(personData?.cases?.accommodations || []),
    ],
    [personData],
  );

  React.useEffect(() => {
    //Initial formatting of the data
    const allDocs = get(personData, 'documents.letters', []).concat(
      get(personData, 'documents.documents', []),
    );

    //Determine if the document has a claimId or a leaveId and assign that value to a common caseId property
    const allDocsWithCaseId = allDocs.map((document: DocumentProps) => {
      const caseId = document.claimId ? document.claimId : document.leaveId;
      document.caseId = caseId;
      return document;
    });

    setInitialDocuments(allDocsWithCaseId);
  }, [personData]);

  React.useEffect(() => {
    if (!ntnToRoute || !claimList?.length) {
      setSelectedClaim(defaultOption);
      return;
    }

    const normalizedNtn = normalize(ntnToRoute);
    const match = claimList.find((c: any) =>
      hasBaseNtn(c.caseId, normalizedNtn),
    );

    setSelectedClaim(match ?? defaultOption);
    setPage({ skip: 0, take: page.take });
  }, [ntnToRoute, claimList, page.take]);

  // Effective filter comes from selected claim if set; else from route
  const effectiveFilterKey = React.useMemo(() => {
    if (selectedClaim && selectedClaim.caseId !== defaultOption.caseId) {
      return normalize(selectedClaim.caseId);
    }

    return normalize(ntnToRoute);
  }, [selectedClaim, ntnToRoute]);

  const uploadDocuments = useUploadDocuments();

  // Merge & sort docs once; filter on demand
  const sortedDocuments = React.useMemo(
    () => [...initialDocuments, ...uploadDocuments].sort(sortDescending),
    [initialDocuments, uploadDocuments],
  );

  const displayDocuments = React.useMemo(() => {
    if (!effectiveFilterKey) return sortedDocuments;

    return sortedDocuments.filter(
      (doc) =>
        Boolean(doc.caseId) && sameNtnFamily(doc.caseId!, effectiveFilterKey),
    );
  }, [sortedDocuments, effectiveFilterKey]);

  const handlePageChange = (e: PageChangeEvent) => {
    setPage({
      skip: e.skip,
      take: e.take,
    });
  };

  const openModal = useCallback((link: string) => {
    setModalLink(link);
    setDisplayModal(true);
  }, []);

  const itemRenderer = useCallback(
    (itemProps: Omit<IItemRenderProps, 'openModal'>) => (
      <DocumentItemRender {...itemProps} openModal={openModal} />
    ),
    [openModal],
  );

  const closeModal = () => {
    setDisplayModal(false);
  };

  const handleFilterByClaim = (e: DropDownListChangeEvent) => {
    const selection = extractSelectedNTN(e.target.value.caseId);

    setPage({ skip: 0, take: page.take });
    if (selection) {
      history.replace(`${enums.AppPaths.documents}/${selection}`);
    } else {
      history.replace(enums.AppPaths.documents);
    }
  };

  return (
    <GridContainer id={domIdsStatic.rootNode}>
      <ColumnContainer cols={9}>
        <ItalicText>
          {`Please note, some documents may not be visible in the portal due to compliance restrictions. Any viewable document will be available within five business days from receipt.`}
        </ItalicText>
      </ColumnContainer>
      <ColumnContainer cols={9}>
        <ContentContainer>
          <TitleBar>
            <Title>Document</Title>
            <ClaimsDropDownList
              caseList={claimList}
              defaultOption={defaultOption}
              value={selectedClaim}
              onChange={handleFilterByClaim}
              viewAll
              processedProps
              disabled={isRefreshing}
            />
          </TitleBar>

          <div className="tw-relative">
            {isRefreshing && (
              <LoadingCover>
                <FadeLoader
                  height={14}
                  width={5}
                  radius={2}
                  margin={0}
                  color={colors.blue}
                />
              </LoadingCover>
            )}
            {displayDocuments.length > 0 ? (
              <ListViewBody>
                <ListView
                  data={displayDocuments?.slice(skip, skip + take)}
                  item={itemRenderer}
                  header={Header}
                  style={{ border: 0, position: 'initial' }}
                ></ListView>
                <PagerSection
                  skip={skip}
                  take={take}
                  handlePageChange={handlePageChange}
                  elements={displayDocuments}
                />
              </ListViewBody>
            ) : (
              <NoDocumentsContainer>
                <img src={DocumentGraphic} alt="" width="179" height="148" />
                <ItalicText>
                  {`You will be able to view your documents & letters here once you start to receive them.`}
                </ItalicText>
              </NoDocumentsContainer>
            )}
          </div>

          {displayModal && (
            <Dialog
              id={`document-available-modal`}
              onClose={closeModal}
              title={'Document Available'}
            >
              <Text fontSize="base" className="tw-p-4 tw-pb-5">
                Your document has been securely retrieved and is now available
                for viewing.
              </Text>
              <ButtonStyledLink
                id={`open-document-link`}
                href={modalLink}
                target="_blank"
              >
                Open Document
              </ButtonStyledLink>
            </Dialog>
          )}
        </ContentContainer>
      </ColumnContainer>
    </GridContainer>
  );
};

/**
 * Customer Header for the ListView component
 */
const Header = () => {
  return (
    <div className="tw-text-unm-adhoc2 tw-text-sm tw-m-0 tw-items-center tw-hidden md:tw-flex">
      <HeaderTextSmall fontSize="lg" className="tw-w-5/12 md:tw-w-3/12">
        Date
      </HeaderTextSmall>
      <HeaderTextSmall fontSize="lg" className="tw-w-7/12 md:tw-w-5/12">
        Title
      </HeaderTextSmall>
      <HeaderTextSmall
        fontSize="lg"
        className="tw-hidden md:tw-block md:tw-w-4/12"
      >
        Claim ID
      </HeaderTextSmall>
    </div>
  );
};

export default Documents;
